import React, { useEffect, useMemo, useState } from "react";

/**
 * Super Admin Activity Log — production scope
 *
 * Purpose:
 * - Frontend page for viewing Super Admin governance/audit activity
 * - Platform governance activity page
 *
 * Current scope:
 * - Reads from the Super Admin audit endpoint
 * - Uses governance audit metadata
 * - No PHI display
 *
 * Endpoint:
 * - GET /api/super-admin/activity
 */

type ActivityItem = {
  auditId: number;
  userId: number | null;
  actorEmail: string | null;
  actorDisplayName: string | null;
  tableName: string | null;
  action: string | null;
  timestamp: string | null;
};

type ActivityResponse = {
  items?: ActivityItem[];
  error?: string;
};

const TABLE_FILTER_OPTIONS = [
  { value: "", label: "All areas" },
  { value: "AccessRequest", label: "Access requests" },
  { value: "Clinic", label: "Clinics" },
  { value: "UserRole", label: "User roles" },
];

export default function SuperAdminActivityPage() {
  const [items, setItems] = useState<ActivityItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [tableName, setTableName] = useState("");
  const [actionSearch, setActionSearch] = useState("");
  const [limit, setLimit] = useState("100");

  const queryString = useMemo(() => {
    const params = new URLSearchParams();

    const safeLimit = Number.parseInt(limit, 10);
    params.set(
      "limit",
      Number.isFinite(safeLimit)
        ? String(Math.min(Math.max(safeLimit, 1), 200))
        : "100"
    );

    if (tableName.trim()) params.set("tableName", tableName.trim());
    if (actionSearch.trim()) params.set("action", actionSearch.trim());

    return params.toString();
  }, [actionSearch, limit, tableName]);

  async function loadActivity(mode: "initial" | "refresh" = "initial") {
    try {
      if (mode === "initial") {
        setLoading(true);
      } else {
        setRefreshing(true);
      }

      setError(null);

      const res = await fetch(`/api/super-admin/activity?${queryString}`, {
        method: "GET",
        credentials: "include",
      });

      const json = (await res.json().catch(() => ({}))) as ActivityResponse;

      if (!res.ok) {
        throw new Error(
          json?.error || `Unable to load activity log (${res.status})`
        );
      }

      setItems(Array.isArray(json.items) ? json.items : []);
    } catch (e: any) {
      setError(e?.message || "Unable to load activity log.");
      setItems([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }

  useEffect(() => {
    loadActivity("initial");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasFilters = Boolean(tableName.trim() || actionSearch.trim());

  return (
    <div>
      {/* Page header */}
      <div style={{ marginBottom: 20 }}>
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            padding: "5px 10px",
            borderRadius: 999,
            background: "#eaf4ff",
            border: "1px solid #cfe6ff",
            color: "#1669b3",
            fontSize: 12,
            fontWeight: 650,
            marginBottom: 10,
          }}
        >
          Governance activity
        </div>

        <h1
          style={{
            margin: 0,
            fontSize: 24,
            fontWeight: 650,
            color: "#102a43",
            letterSpacing: "-0.02em",
          }}
        >
          Activity Log
        </h1>

        <p
          style={{
            marginTop: 8,
            color: "#6b7a94",
            lineHeight: 1.55,
            maxWidth: 820,
            fontSize: 14,
            fontWeight: 450,
          }}
        >
          Governance activity view for Super Admin oversight. This page shows
          audit metadata such as clinic approvals, clinic creation, role
          changes, and related governance actions. It should not display patient
          clinical data or raw PHI.
        </p>
      </div>

      {/* Current status panel */}
      <section
        style={{
          padding: 18,
          borderRadius: 18,
          border: "1px solid #b9dcff",
          background: "#f7fbff",
          boxShadow: "0 8px 24px rgba(15, 80, 135, 0.04)",
          marginBottom: 18,
        }}
      >
        <div
          style={{
            fontSize: 16,
            fontWeight: 650,
            color: "#0f5087",
            letterSpacing: "-0.01em",
          }}
        >
          Audit endpoint connected
        </div>

        <p
          style={{
            marginTop: 6,
            marginBottom: 0,
            color: "#6b7a94",
            fontSize: 13,
            lineHeight: 1.55,
            maxWidth: 850,
            fontWeight: 450,
          }}
        >
          This page reads from the Super Admin activity endpoint backed by
          governance audit records. It provides a focused view of platform
          actions while keeping patient clinical information out of the activity
          log.
        </p>
      </section>

      {/* Scope cards */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
          gap: 14,
          marginBottom: 18,
        }}
      >
        <ScopeCard
          title="Captured events"
          items={[
            "Clinic Admin request approved or rejected",
            "Clinic created from approval workflow",
            "Clinic geocoded at approval",
            "Clinic Admin role granted",
            "Clinic Admin roles replaced",
          ]}
        />

        <ScopeCard
          title="Fields shown"
          items={[
            "Audit ID",
            "Actor",
            "Affected table or area",
            "Action summary",
            "Timestamp",
          ]}
        />

        <ScopeCard
          title="Data guardrails"
          items={[
            "No raw patient PHI",
            "No request payload dumps",
            "No token values",
            "No clinical notes",
            "Metadata stays minimal",
          ]}
        />
      </div>

      {/* Activity table */}
      <section
        style={{
          borderRadius: 18,
          border: "1px solid #d1d9e6",
          background: "#ffffff",
          boxShadow: "0 8px 24px rgba(15, 80, 135, 0.05)",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            padding: "16px 18px",
            borderBottom: "1px solid #e5edf5",
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 14,
            flexWrap: "wrap",
          }}
        >
          <div>
            <div
              style={{
                fontSize: 16,
                fontWeight: 650,
                color: "#102a43",
                letterSpacing: "-0.01em",
              }}
            >
              Governance activity
            </div>
            <div
              style={{
                marginTop: 4,
                fontSize: 13,
                color: "#6b7a94",
                fontWeight: 450,
              }}
            >
              {loading
                ? "Loading recent audit events…"
                : `${items.length} audit event${items.length === 1 ? "" : "s"} shown.`}
            </div>
          </div>

          <button
            type="button"
            onClick={() => loadActivity("refresh")}
            disabled={loading || refreshing}
            style={{
              padding: "9px 12px",
              borderRadius: 12,
              border: "1px solid #d1d9e6",
              background: "#ffffff",
              color: "#102a43",
              fontSize: 13,
              fontWeight: 650,
              cursor: loading || refreshing ? "not-allowed" : "pointer",
              opacity: loading || refreshing ? 0.65 : 1,
              whiteSpace: "nowrap",
            }}
          >
            {refreshing ? "Refreshing…" : "Refresh"}
          </button>
        </div>

        {/* Filters */}
        <div
          style={{
            padding: 14,
            borderBottom: "1px solid #e5edf5",
            background: "#f8fafc",
            display: "grid",
            gridTemplateColumns: "minmax(180px, 220px) minmax(220px, 1fr) minmax(120px, 140px) auto",
            gap: 10,
            alignItems: "end",
          }}
        >
          <label style={filterLabelStyle}>
            Area
            <select
              value={tableName}
              onChange={(e) => setTableName(e.target.value)}
              style={filterControlStyle}
            >
              {TABLE_FILTER_OPTIONS.map((option) => (
                <option key={option.value || "all"} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <label style={filterLabelStyle}>
            Action contains
            <input
              value={actionSearch}
              onChange={(e) => setActionSearch(e.target.value)}
              placeholder="Example: approved, ClinicID, role granted"
              style={filterControlStyle}
            />
          </label>

          <label style={filterLabelStyle}>
            Limit
            <input
              value={limit}
              onChange={(e) => setLimit(e.target.value)}
              inputMode="numeric"
              style={filterControlStyle}
            />
          </label>

          <button
            type="button"
            onClick={() => loadActivity("refresh")}
            disabled={loading || refreshing}
            style={{
              padding: "10px 12px",
              borderRadius: 12,
              border: "1px solid #1669b3",
              background: "#1669b3",
              color: "#ffffff",
              fontSize: 13,
              fontWeight: 650,
              cursor: loading || refreshing ? "not-allowed" : "pointer",
              opacity: loading || refreshing ? 0.65 : 1,
              whiteSpace: "nowrap",
            }}
          >
            Apply
          </button>
        </div>

        {error ? (
          <div
            style={{
              margin: 14,
              padding: 14,
              borderRadius: 14,
              border: "1px solid #fecaca",
              background: "#fff1f2",
              color: "#991b1b",
              fontSize: 13,
              lineHeight: 1.5,
              fontWeight: 500,
            }}
          >
            {error}
          </div>
        ) : null}

        <div style={{ overflowX: "auto" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              minWidth: 860,
            }}
          >
            <thead>
              <tr style={{ background: "#f8fafc" }}>
                <Th>Audit ID</Th>
                <Th>Action</Th>
                <Th>Actor</Th>
                <Th>Area</Th>
                <Th>Timestamp</Th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <Td colSpan={5}>Loading activity…</Td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <Td colSpan={5}>
                    {hasFilters
                      ? "No audit events match the selected filters."
                      : "No audit events found yet."}
                  </Td>
                </tr>
              ) : (
                items.map((event) => (
                  <tr key={event.auditId}>
                    <Td mono>{event.auditId}</Td>
                    <Td strong>{event.action || "—"}</Td>
                    <Td>
                      {event.actorDisplayName ||
                        event.actorEmail ||
                        formatUserId(event.userId)}
                    </Td>
                    <Td>{event.tableName || "—"}</Td>
                    <Td>{formatTimestamp(event.timestamp)}</Td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

/* ---------- Local UI components ---------- */

const filterLabelStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 6,
  color: "#64748b",
  fontSize: 12,
  fontWeight: 650,
  minWidth: 0,
};

const filterControlStyle: React.CSSProperties = {
  width: "100%",
  minWidth: 0,
  borderRadius: 12,
  border: "1px solid #d1d9e6",
  background: "#ffffff",
  color: "#102a43",
  padding: "9px 10px",
  fontSize: 13,
  fontWeight: 500,
  outline: "none",
  boxSizing: "border-box",
};

function ScopeCard({ title, items }: { title: string; items: string[] }) {
  return (
    <section
      style={{
        padding: 18,
        borderRadius: 18,
        border: "1px solid #d1d9e6",
        background: "#ffffff",
        boxShadow: "0 8px 24px rgba(15, 80, 135, 0.05)",
      }}
    >
      <div
        style={{
          fontSize: 15,
          fontWeight: 650,
          color: "#102a43",
          letterSpacing: "-0.01em",
          marginBottom: 12,
        }}
      >
        {title}
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {items.map((item) => (
          <div
            key={item}
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 9,
              color: "#102a43",
              fontSize: 13,
              lineHeight: 1.45,
              fontWeight: 500,
            }}
          >
            <span
              aria-hidden
              style={{
                width: 7,
                height: 7,
                borderRadius: 999,
                background: "#1669b3",
                marginTop: 6,
                flex: "0 0 auto",
              }}
            />
            <span>{item}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th
      style={{
        padding: "11px 14px",
        borderBottom: "1px solid #e2e8f0",
        color: "#64748b",
        fontSize: 12,
        fontWeight: 650,
        textAlign: "left",
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  strong = false,
  mono = false,
  colSpan,
}: {
  children: React.ReactNode;
  strong?: boolean;
  mono?: boolean;
  colSpan?: number;
}) {
  return (
    <td
      colSpan={colSpan}
      style={{
        padding: "13px 14px",
        borderBottom: "1px solid #eef2f7",
        color: strong ? "#102a43" : "#475569",
        fontSize: 13,
        fontWeight: strong ? 650 : 500,
        verticalAlign: "top",
        fontFamily: mono
          ? "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace"
          : undefined,
      }}
    >
      {children}
    </td>
  );
}

function formatTimestamp(value: string | null): string {
  if (!value) return "—";

  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);

  return d.toLocaleString("en-CA", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatUserId(userId: number | null): string {
  if (!userId) return "—";
  return `User #${userId}`;
}